/* eslint-disable no-unused-vars */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-expressions */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Empty,
  Skeleton,
  Result,
  DatePicker,
  Tabs,
  Card,
  Statistic,
  Row,
  Col,
  Pagination,
  Tag,
  Input,
  Modal,
  Descriptions,
  Button,
  Image
} from 'antd';
import dayjs from 'dayjs';
import axios from 'axios'; // ✅ Import manquant
import { useDispatch } from 'react-redux';
import useFetchData from '../../hooks/useFetchData';
import { getRoleResponse } from '../../utils/format';
import { useSocketContext } from '../../context/SocketContext';

const { RangePicker } = DatePicker;

// 🔹 Récupérer la date du jour
const gettoday = () => {
  const today = dayjs();
  return {
    startDate: today.format('YYYY-MM-DD'),
    endDate: today.format('YYYY-MM-DD')
  };
};

const DAYS = [
  { label: 'Dimanche', value: 0 },
  { label: 'Lundi', value: 1 },
  { label: 'Mardi', value: 2 },
  { label: 'Mercredi', value: 3 },
  { label: 'Jeudi', value: 4 },
  { label: 'Vendredi', value: 5 },
  { label: 'Samedi', value: 6 }
];

function Attendance() {
  const dispatch = useDispatch();
  const today = gettoday();
  const { arrivedUser } = useSocketContext();

  const [query, setQuery] = useState({
    startDate: today.startDate,
    endDate: today.endDate,
    status: '',
    dayOfWeek: 0 // 🔹 par défaut dimanche
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [presentPage, setPresentPage] = useState(1);
  const [absentPage, setAbsentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, error, response] = useFetchData(
    `/attendance/list?startDate=${query.startDate}&endDate=${query.endDate}&status=${query.status}&dayOfWeek=${query.dayOfWeek}`,
    query.startDate + query.endDate + query.status + query.dayOfWeek
  );

  const [localResponse, setLocalResponse] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [imageUrl, setImageUrl] = useState(null);
  const [reloading, setReloading] = useState(false);

  // 🔹 Charger image selon la source
  const loadImage = async (user = selectedUser) => {
    if (!user?.image) return setImageUrl(null);
    try {
      setReloading(true);
      const url = user.image;

      // Jotform
      const cookie = getJotformCookie(url);
      if (cookie) {
        const res = await axios.get(url, { responseType: 'blob', headers: { Cookie: cookie } });
        const blobUrl = URL.createObjectURL(res.data);
        setImageUrl(blobUrl);
        return;
      }

      // Google Drive
      if (url.includes('drive.google.com')) {
        setImageUrl(convertGoogleDriveUrl(url));
        return;
      }

      // URL directe
      setImageUrl(url);
    } catch (err) {
      console.error('Erreur chargement image :', err);
      setImageUrl(null);
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    document.title = 'Rapport de présence';
  }, []);

  useEffect(() => {
    if (response?.data) {
      setLocalResponse(response);
    }
  }, [response]);

  useEffect(() => {
    if (arrivedUser && localResponse) {
      setLocalResponse((prev) => {
        if (!prev?.data?.rows?.present || !prev?.data?.rows?.absent) return prev;

        const presentList = prev.data.rows.present.users || [];
        const absentList = prev.data.rows.absent.users || [];

        const exists = presentList.some(
          (u) => u._id === arrivedUser._id || u.nfcCard === arrivedUser.nfcCard
        );
        if (exists) return prev;

        const updatedAbsentList = absentList.filter(
          (u) => u._id !== arrivedUser._id && u.nfcCard !== arrivedUser.nfcCard
        );

        const updated = {
          ...prev,
          data: {
            ...prev.data,
            rows: {
              ...prev.data.rows,
              present: {
                ...prev.data.rows.present,
                count: prev.data.rows.present.count + 1,
                users: [arrivedUser, ...presentList]
              },
              absent: {
                ...prev.data.rows.absent,
                count: updatedAbsentList.length,
                users: updatedAbsentList
              }
            }
          }
        };
        return updated;
      });
    }
  }, [arrivedUser, localResponse]);

  const columns = useMemo(
    () => [
      {
        title: "Heure d'arrivée",
        key: 'time',
        render: (_, record) => (record?.lastScanTime ? dayjs(record.lastScanTime).format('ddd DD MMM - HH:mm') : '-')
      },
      {
        title: 'Nom',
        key: 'firstname',
        render: (_, record) => {
          const civility = record?.civility || '';
          const firstname = record?.firstname || '';
          const quartier = record?.quartier ? `(${record?.quartier})` : '';
          const role = record?.role || '';
          return (
            <span>
              {`${civility} ${firstname} ${quartier}`}
              <span className='float-right'>
                <Tag color={getRoleResponse(role, true).color}>{role}</Tag>
              </span>
            </span>
          );
        }
      },
      {
        title: 'Téléphone',
        dataIndex: 'phone',
        key: 'phone'
      }
    ],
    []
  );

  const present = localResponse?.data?.rows?.present || { count: 0, users: [] };
  const absent = localResponse?.data?.rows?.absent || { count: 0, users: [] };

  const filterUsers = (users) => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.civility || ''} ${user.firstname || ''} ${user.quartier || ''} ${user.role || ''}`.toLowerCase();
      return fullName.includes(term);
    });
  };

  const presentFiltered = filterUsers(present.users);
  const absentFiltered = filterUsers(absent.users);

  const paginate = (arr, page) => arr.slice((page - 1) * pageSize, page * pageSize);

  const presentPaginated = paginate(presentFiltered, presentPage);
  const absentPaginated = paginate(absentFiltered, absentPage);

  const showUserDetail = async (user) => {
    setSelectedUser(user);
    await loadImage(user);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedUser(null);
    setImageUrl(null);
  };

  return (
    <div>
      <Card className='mb-4'>
        <Row gutter={16}>
          <Col>
            <Input
              placeholder='Rechercher Nom / Quartier / Rôle...'
              value={searchTerm}
              allowClear
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPresentPage(1);
                setAbsentPage(1);
              }}
              style={{ width: 300 }}
            />
          </Col>
        </Row>
      </Card>

      {error ? (
        <Result title='Échec du chargement' subTitle={error} status='error' />
      ) : (
        <Skeleton loading={loading} active paragraph={{ rows: 10 }}>
          {query.status === '' && (
            <Row gutter={16} style={{ marginBottom: 20 }}>
              <Col span={12}>
                <Card>
                  <Statistic
                    title='Présents'
                    value={presentFiltered.length || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card>
                  <Statistic
                    title='Absents'
                    value={absentFiltered.length || 0}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          <Tabs
            defaultActiveKey='present'
            items={[
              {
                key: 'present',
                label: 'Présents',
                children:
                  presentFiltered.length === 0 ? (
                    <Empty description='Aucun utilisateur présent' />
                  ) : (
                    <>
                      <Table
                        rowKey={(record) => record._id || record.nfcCard}
                        columns={columns}
                        dataSource={presentPaginated}
                        pagination={false}
                        bordered
                        onRow={(record) => ({
                          onClick: () => showUserDetail(record),
                          style: { cursor: 'pointer' }
                        })}
                      />
                      <Pagination
                        className='my-5'
                        current={presentPage}
                        total={presentFiltered.length}
                        pageSize={pageSize}
                        onChange={(page, size) => {
                          setPresentPage(page);
                          setPageSize(size);
                        }}
                      />
                    </>
                  )
              },
              {
                key: 'absent',
                label: 'Absents',
                children:
                  absentFiltered.length === 0 ? (
                    <Empty description='Aucun utilisateur absent' />
                  ) : (
                    <>
                      <Table
                        rowKey={(record) => record._id || record.nfcCard}
                        columns={columns}
                        dataSource={absentPaginated}
                        pagination={false}
                        bordered
                        onRow={(record) => ({
                          onClick: () => showUserDetail(record),
                          style: { cursor: 'pointer' }
                        })}
                      />
                      <Pagination
                        className='my-5'
                        current={absentPage}
                        total={absentFiltered.length}
                        pageSize={pageSize}
                        onChange={(page, size) => {
                          setAbsentPage(page);
                          setPageSize(size);
                        }}
                      />
                    </>
                  )
              }
            ]}
          />
        </Skeleton>
      )}

      {/* 🔹 Modal détail utilisateur */}
      <Modal
        title="Détails de l'utilisateur"
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        {selectedUser ? (
          <div className='flex flex-col sm:flex-row gap-4'>
            <div className='w-full sm:w-[40%] flex flex-col items-center'>
              {imageUrl ? (
                <Image
                  width='100%'
                  src={imageUrl}
                  className='rounded-lg object-cover aspect-[3/4]'
                  alt={`${selectedUser.firstname || 'Utilisateur'}`}
                />
              ) : (
                <div className='w-full aspect-[3/4] bg-gray-200 rounded-lg flex flex-col items-center justify-center text-6xl font-bold text-gray-500'>
                  <span>{selectedUser?.firstname?.[0]?.toUpperCase() || '?'}</span>
                  <Button
                    type='default'
                    size='small'
                    loading={reloading}
                    onClick={() => loadImage(selectedUser)}
                    className='mt-2'
                  >
                    Recharger l’image
                  </Button>
                </div>
              )}
            </div>

            <div className='flex-1'>
              <Descriptions bordered column={1} size='small'>
                <Descriptions.Item label='Nom'>
                  {selectedUser.civility}
                  {' '}
                  {selectedUser.firstname}
                </Descriptions.Item>
                <Descriptions.Item label='Quartier'>
                  {selectedUser.quartier || '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Rôle'>
                  <Tag color={getRoleResponse(selectedUser.role, true).color}>
                    {selectedUser.role}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label='Téléphone'>
                  {selectedUser.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Dernier scan'>
                  {selectedUser.lastScanTime ?
                    dayjs(selectedUser.lastScanTime).format('DD/MM/YYYY HH:mm') :
                    '-'}
                </Descriptions.Item>
                <Descriptions.Item label='Carte NFC'>
                  {selectedUser.nfcCard || '-'}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        ) : (
          <Empty description='Aucun utilisateur sélectionné' />
        )}
      </Modal>
    </div>
  );
}

export default React.memo(Attendance);

// ✅ Cookies & outils externes
const jotformCookiesByUser = {
  andersenjessy500:
    'JF_SESSION_USERNAME=andersenjessy500; JOTFORM_SESSION=581615e8-0946-2182-bc5a-cde8f693',
  andersenjessy:
    'JF_SESSION_USERNAME=andersenjessy; JOTFORM_SESSION=a04eeee9-f7c4-5e66-51c6-072986ef'
};

const convertGoogleDriveUrl = (url) => {
  const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1000`;
  }
  return url;
};

const getJotformCookie = (url) => {
  if (!url.includes('jotform.com')) return null;
  return url.includes('andersenjessy500') ?
    jotformCookiesByUser.andersenjessy500 :
    jotformCookiesByUser.andersenjessy || null;
};
